/**
 * Capsule unsaved-work guard.
 *
 * Until the studio kept a copy, this dialog was the *only* thing standing between a
 * creator and a lost evening — and a warning does not protect anything, it only
 * announces the loss. Now the studio keeps a copy on this device as the capsule is
 * written, but a copy is *this device's*, and it is a draft that makes a capsule
 * real. So the dialog still stands between the creator and anything that would drop
 * the work in progress, and both cases it covers offer the same two honest
 * outcomes: keep it as a draft, or throw it away on purpose.
 */
import {View} from 'react-native'
import {Trans, useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

export function CapsuleLeaveDialog({
  control,
  busy,
  slideCount,
  /** What the pending action would do, which decides the wording. */
  intent,
  /** Title of the draft being opened, when the intent is to replace. */
  draftTitle,
  onSaveDraft,
  onDiscard,
}: {
  control: Dialog.DialogControlProps
  /** A draft save is in flight — the dialog stays open until it settles. */
  busy: boolean
  slideCount: number
  intent: 'leave' | 'replace'
  draftTitle?: string
  onSaveDraft: () => void
  onDiscard: () => void
}) {
  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <DialogInner
        busy={busy}
        slideCount={slideCount}
        intent={intent}
        draftTitle={draftTitle}
        onSaveDraft={onSaveDraft}
        onDiscard={onDiscard}
      />
    </Dialog.Outer>
  )
}

function DialogInner({
  busy,
  slideCount,
  intent,
  draftTitle,
  onSaveDraft,
  onDiscard,
}: {
  busy: boolean
  slideCount: number
  intent: 'leave' | 'replace'
  draftTitle?: string
  onSaveDraft: () => void
  onDiscard: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const control = Dialog.useDialogContext()
  const replacing = intent === 'replace'

  return (
    <Dialog.ScrollableInner
      style={web({maxWidth: 420})}
      label={l`Unsaved capsule`}>
      <View style={[a.gap_2xl]}>
        <View style={[a.gap_sm]}>
          <Text style={[a.font_bold, a.text_2xl]}>
            {replacing ? (
              <Trans>Open this draft instead?</Trans>
            ) : (
              <Trans>Leave without saving?</Trans>
            )}
          </Text>
          <Text
            style={[t.atoms.text_contrast_medium, a.text_md, a.leading_snug]}>
            {replacing ? (
              <Trans>
                Loading {draftTitle ?? l`this draft`} replaces the capsule you
                are working on, and your changes here have not been saved.
              </Trans>
            ) : (
              <Trans>
                This capsule has {slideCount} unsaved{' '}
                {slideCount === 1 ? 'slide' : 'slides'} and has not been
                published. A copy is kept on this device, but saving a draft is
                what keeps it on every device.
              </Trans>
            )}
          </Text>
        </View>

        <View style={[a.gap_sm]}>
          <Button
            label={l`Save as draft`}
            accessibilityHint={l`Keeps this capsule private so you can finish it later`}
            disabled={busy}
            onPress={onSaveDraft}
            size="large"
            variant="solid"
            color="primary"
            testID="capsuleSaveDraftBtn">
            <ButtonText>
              <Trans>Save as draft</Trans>
            </ButtonText>
            {busy ? <ButtonIcon icon={Loader} /> : null}
          </Button>
          <Button
            label={replacing ? l`Discard and open` : l`Discard`}
            accessibilityHint={l`Drops the unsaved work`}
            disabled={busy}
            onPress={onDiscard}
            size="large"
            variant="solid"
            color="negative_subtle">
            <ButtonText>
              {replacing ? (
                <Trans>Discard and open</Trans>
              ) : (
                <Trans>Discard</Trans>
              )}
            </ButtonText>
          </Button>
          <Button
            label={l`Keep editing`}
            accessibilityHint={l`Returns to the capsule creator`}
            disabled={busy}
            onPress={() => control.close()}
            size="large"
            variant="ghost"
            color="secondary">
            <ButtonText>
              <Trans>Keep editing</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
